import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import '@theme/component-quantity-selector';

class CmProductPurchase extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.cleanup();
    this.initialize();
  }

  initialize() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector('form');
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
    this.quantitySelector = this.querySelector('quantity-selector-component');
    this.cartQuantities = JSON.parse(this.querySelector('[data-cart-quantities]')?.textContent || '{}');
    this.form.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
      if (event.target === this.quantitySelector) this.update(false);
    }, { signal });

    this.button = this.querySelector('[type="submit"]');
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency', currency: this.dataset.currency, currencyDisplay: 'narrowSymbol',
    });
    this.form.addEventListener('change', (event) => {
      const variantChanged = event.target.matches('input[name="id"]');
      if (event.target.matches('[data-sphere]')) {
        for (const select of this.querySelectorAll('[data-sphere]')) {
          if (select.dataset.sphere === event.target.dataset.sphere) select.value = event.target.value;
        }
      }
      if (variantChanged) {
        for (const select of this.querySelectorAll('[data-sphere]')) select.value = event.target.value;
      }
      this.update(variantChanged);
      if (variantChanged) {
        const url = new URL(location.href);
        url.searchParams.set('variant', this.querySelector('input[name="id"]:checked').value);
        history.replaceState({}, '', url);
      }
    }, { signal });
    if (this.dataset.nativeCart !== 'true') {
      this.form.addEventListener('submit', (event) => this.submit(event), { signal });
    }
    this.update(false);
  }

  format(cents) {
    const formatted = this.money.format(cents / 100);
    return this.dataset.currency === 'AUD' && formatted.startsWith('$') ? `A${formatted}` : formatted;
  }

  update(variantChanged) {
    const variant = this.variants.find((item) => String(item.id) === this.querySelector('input[name="id"]:checked')?.value);
    if (!variant) return;
    this.variant = variant;
    const rule = variant.quantity_rule || { min: 1, increment: 1, max: null };
    const minimum = rule.min || 1;
    const step = rule.increment || 1;
    const maximum = Math.min(rule.max ?? 3, 3);
    const choices = [...this.querySelectorAll('input[name="quantity"][type="radio"]')];
    const quantityInput = this.quantitySelector?.querySelector('input[name="quantity"]');
    if (quantityInput && this.quantitySelector.initialized) {
      quantityInput.dataset.cartQuantity = String(this.cartQuantities[variant.id] || 0);
      this.quantitySelector.updateConstraints(String(minimum), rule.max ? String(rule.max) : null, String(step));
      quantityInput.disabled = !variant.available || this.busy;
      if (quantityInput.disabled) {
        for (const button of this.quantitySelector.querySelectorAll('button')) button.disabled = true;
      }
    }
    choices.forEach((radio) => {
      const quantity = Number(radio.value);
      radio.disabled = !variant.available || quantity < minimum || quantity > maximum || (quantity - minimum) % step !== 0;
      const baseUnitPrice = (variant.quantity_price_breaks || []).reduce((price, tier) => quantity >= tier.minimum_quantity ? tier.price : price, variant.price);
      // Matches the active Shopify automatic discounts; Shopify owns final cart totals.
      const discount = quantity >= 3 ? Number(this.dataset.trioDiscount || 0) : quantity === 2 ? Number(this.dataset.pairDiscount || 0) : 0;
      const unitPrice = this.dataset.currency === 'AUD' ? Math.max(0, baseUnitPrice - discount) : baseUnitPrice;
      radio.dataset.total = unitPrice * quantity;
      const card = radio.closest('label');
      card.querySelector('[data-bundle-unit]').textContent = this.format(unitPrice);
      card.querySelector('[data-bundle-total]').textContent = this.format(unitPrice * quantity);
      const original = card.querySelector('[data-bundle-original]');
      const referencePrice = Math.max(variant.compare_at_price || 0, variant.price);
      original.textContent = this.format(referencePrice * quantity);
      original.hidden = unitPrice >= referencePrice;
      const saving = card.querySelector('[data-bundle-saving]');
      if (saving) {
        saving.hidden = unitPrice >= referencePrice;
        saving.textContent = this.labels.save.replace('[amount]', this.format((referencePrice - unitPrice) * quantity).replace(/\.00$/, ''));
      }
    });
    if (!choices.some((radio) => radio.checked && !radio.disabled)) {
      choices.forEach((radio) => { radio.checked = false; });
      const available = choices.find((radio) => !radio.disabled);
      if (available) available.checked = true;
    }
    const selected = choices.find((radio) => radio.checked && !radio.disabled);
    const quantity = Number(quantityInput?.value || selected?.value || 1);
    for (const panel of this.querySelectorAll('[data-bundle-colours]')) {
      const active = Number(panel.dataset.bundleColours) === quantity;
      panel.hidden = !active;
      for (const select of panel.querySelectorAll('select')) select.disabled = !active || this.busy;
    }
    this.querySelector('.cm-trial-colours').hidden = !quantityInput && quantity > 1;
    // Express product checkout only represents a single variant. Wallets remain available in checkout.
    this.querySelector('.cm-purchase__express').hidden = !quantityInput && quantity > 1;
    const items = this.getItems(quantity);
    const valid = items.length > 0 && items.every((item) => {
      const choice = this.variants.find((entry) => String(entry.id) === String(item.id));
      const limit = choice?.quantity_rule || { min: 1, increment: 1 };
      return choice?.available && Number.isSafeInteger(item.quantity) && item.quantity >= (limit.min || 1) && (!limit.max || item.quantity <= limit.max) && (item.quantity - (limit.min || 1)) % (limit.increment || 1) === 0;
    }) && (!quantityInput || !this.quantitySelector.initialized || this.quantitySelector.canAddToCart().canAdd);
    const discount = this.dataset.currency === 'AUD' ? quantity >= 3 ? Number(this.dataset.trioDiscount) : quantity === 2 ? Number(this.dataset.pairDiscount) : 0 : 0;
    const selectedTotal = items.reduce((sum, item) => {
      const choice = this.variants.find((entry) => String(entry.id) === String(item.id));
      const price = (choice.quantity_price_breaks || []).reduce((amount, tier) => item.quantity >= tier.minimum_quantity ? tier.price : amount, choice.price);
      return sum + Math.max(0, price - discount) * item.quantity;
    }, 0);
    const referenceTotal = items.reduce((sum, item) => {
      const choice = this.variants.find((entry) => String(entry.id) === String(item.id));
      return sum + Math.max(choice.compare_at_price || 0, choice.price) * item.quantity;
    }, 0);
    const total = this.format(selectedTotal);
    this.querySelector('[data-selected-total]').textContent = total;
    const saleSaving = this.querySelector('[data-selected-saving]');
    if (saleSaving) {
      const savings = referenceTotal - selectedTotal;
      saleSaving.hidden = savings <= 0;
      saleSaving.textContent = `SAVE ${this.format(Math.max(0, savings)).replace(/\.00$/, '')}`;
    }
    const rrp = this.querySelector('[data-selected-rrp]');
    if (rrp) {
      rrp.hidden = referenceTotal <= selectedTotal;
      rrp.querySelector('del').textContent = this.format(referenceTotal);
    }
    this.button.disabled = (!selected && !quantityInput) || !valid || this.busy;
    this.querySelector('[data-button-label]').textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOut;
    this.querySelector('[data-button-total]').textContent = total;
    if (variantChanged && variant.featured_image) {
      const gallery = this.querySelector('cm-product-gallery');
      const index = gallery?.slides?.findIndex((slide) => slide.dataset.imageId === String(variant.featured_image.id));
      if (index >= 0) gallery.show(index, true);
      for (const image of this.querySelectorAll('.cm-purchase__miniatures img')) {
        image.removeAttribute('srcset');
        image.src = variant.featured_image.src;
      }
    }
  }

  getItems(quantity = Number(this.quantitySelector?.querySelector('input[name="quantity"]')?.value || this.querySelector('input[name="quantity"]:checked')?.value || 1)) {
    if (this.quantitySelector) return [{ id: String(this.variant.id), quantity }];
    const ids = quantity > 1
      ? [...this.querySelectorAll('[data-bundle-colours="' + quantity + '"] select')].map((select) => select.value)
      : [String(this.variant.id)];
    const grouped = new Map();
    for (const id of ids) grouped.set(id, (grouped.get(id) || 0) + 1);
    return [...grouped].map(([id, count]) => ({ id, quantity: count }));
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy || this.button.disabled) return;
    const error = this.querySelector('[data-error-message]');
    error.hidden = true;
    this.busy = true;
    this.button.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    this.querySelector('[data-button-label]').textContent = this.dataset.adding;
    const body = JSON.stringify({ items: this.getItems() });
    for (const select of this.querySelectorAll('[data-sphere]')) select.disabled = true;
    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body, signal: this.controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
      // Full cart navigation lets Shopify render authoritative totals, discounts and stock.
      location.assign(this.dataset.cartUrl);
    } catch (failure) {
      if (failure.name === 'AbortError') return;
      error.textContent = failure.message || this.dataset.error;
      error.hidden = false;
      this.busy = false;
      this.form.removeAttribute('aria-busy');
      this.update(false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  cleanup() {
    this.controller?.abort();
    this.controller = null;
    this.busy = false;
  }
}
if (!customElements.get('cm-product-purchase')) customElements.define('cm-product-purchase', CmProductPurchase);
